import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * A section that shows a first batch and unfolds in place (the extensions proposal's "Sections
 * dépliables"): "See all · N" right after its title, "Show less" folds it back. No "See all"
 * page. The first batch is one full row of cards at the width it has (`batch="row"`: as many as
 * its grid lays out in a row, 3 until it is measured) or 5 rows of a list; one more is not
 * hidden, it shows. Whether a section is unfolded is remembered per section and
 * per extension, for the user.
 */

/** How many a section shows folded: a row of cards, or five rows of a list. */
export function firstBatch(kind: 'cards' | 'list', layout: 'full' | 'docked' = 'full'): number {
  if (kind === 'list') return 5;
  return layout === 'docked' ? 2 : 3;
}

/**
 * The columns of a grid as the browser lays them out (`grid-template-columns` resolves to one
 * length per track, empty `auto-fill` tracks included); null when it is not a laid-out grid.
 */
export function gridColumns(element: Element | null): number | null {
  if (element === null) return null;
  const tracks = getComputedStyle(element).gridTemplateColumns;
  if (tracks === '' || tracks === 'none' || tracks.includes('repeat(')) return null;
  const count = tracks.split(/\s+/).filter((track) => /^[\d.]+px$/.test(track)).length;
  return count > 0 ? count : null;
}

/**
 * How many cards a row of the grid inside `ref` holds, followed as it resizes; `fallback` until
 * measured (and where nothing lays out, as in tests).
 */
export function useRowBatch(fallback: number) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState<number | null>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (root === null) return;
    const measure = () => setColumns(gridColumns(root.firstElementChild));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  return { ref, batch: columns ?? fallback };
}

/** What a folded section shows: its first batch, unless only one more would be hidden. */
export function foldedCount(total: number, batch: number): number {
  return total <= batch + 1 ? total : batch;
}

const STORAGE_KEY = 'crowdex:expanded-sections';
const listeners = new Set<() => void>();
let cache: Record<string, boolean> | null = null;

function read(): Record<string, boolean> {
  if (cache !== null) return cache;
  try {
    const parsed: unknown = JSON.parse(globalThis.localStorage?.getItem(STORAGE_KEY) ?? '{}');
    cache =
      parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    cache = {};
  }
  return cache;
}

/** Remembers whether the section `key` (`<plugin>/<section>`) is unfolded. */
export function setSectionExpanded(key: string, expanded: boolean): void {
  const { [key]: _was, ...rest } = read();
  cache = expanded ? { ...rest, [key]: true } : rest;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Without storage it holds for this window only.
  }
  for (const listener of listeners) listener();
}

/** Forgets what was read (tests, another window's change). */
export function resetExpandedSections(): void {
  cache = null;
  for (const listener of listeners) listener();
}

/** Whether the section `key` is unfolded, and the way to change it. */
export function useSectionExpanded(key: string): [boolean, (expanded: boolean) => void] {
  const expanded = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => read()[key] === true,
    () => false,
  );
  return [expanded, useCallback((next: boolean) => setSectionExpanded(key, next), [key])];
}

export function ExpandableSection<T>({
  title,
  items,
  batch,
  memoryKey,
  children,
  className,
}: {
  title: string;
  items: readonly T[];
  /**
   * How many it shows folded: a count (`firstBatch`), or `'row'`, one full row of the grid its
   * children render (their first element), whatever the width.
   */
  batch: number | 'row';
  /** Where it is remembered: `<plugin>/<section>`. */
  memoryKey: string;
  /** Renders the items shown. */
  children: (shown: readonly T[]) => ReactNode;
  className?: string;
}) {
  const [expanded, setExpanded] = useSectionExpanded(memoryKey);
  // A section the user unfolds in this render shows at once, before storage answers.
  const [local, setLocal] = useState<boolean | null>(null);
  const open = local ?? expanded;
  const row = useRowBatch(firstBatch('cards'));
  const folded = foldedCount(items.length, batch === 'row' ? row.batch : batch);
  const foldable = folded < items.length;
  const shown = open || !foldable ? items : items.slice(0, folded);
  const id = `section-${memoryKey.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`;
  const toggle = () => {
    setLocal(!open);
    setExpanded(!open);
  };
  return (
    <section aria-labelledby={id} className={className} data-expandable-section={memoryKey}>
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="type-section min-w-0 truncate text-muted-foreground" id={id}>
          {title}
        </h2>
        {foldable && (
          <button
            aria-controls={`${id}-items`}
            aria-expanded={open}
            className="type-meta shrink-0 rounded-control px-1.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={toggle}
            type="button"
          >
            {open ? 'Show less' : `See all · ${items.length}`}
          </button>
        )}
      </div>
      <div id={`${id}-items`} ref={batch === 'row' ? row.ref : undefined}>
        {children(shown)}
      </div>
    </section>
  );
}
